import { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Button,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ClearOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  WifiOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

const { Text } = Typography;

// ── types ─────────────────────────────────────────────────────────────────────

interface TapFlow {
  q: number;    // seq
  t: number;    // unix second
  si: string;   // src ip
  di: string;   // dst ip
  sp: number;   // src port
  dp: number;   // dst port
  pr: number;   // protocol
  fl?: number;  // tcp flags
  b: number;    // bytes
  p: number;    // packets
  sr?: number;  // sampling rate
  sc?: string;  // src country code (ISO 3166-1 alpha-2)
  dc?: string;  // dst country code
}

// ── helpers ───────────────────────────────────────────────────────────────────

const PROTO: Record<number, { name: string; color: string }> = {
  1:   { name: "ICMP",   color: "cyan"    },
  6:   { name: "TCP",    color: "blue"    },
  17:  { name: "UDP",    color: "geekblue"},
  47:  { name: "GRE",    color: "purple"  },
  50:  { name: "ESP",    color: "volcano" },
  58:  { name: "ICMPv6", color: "cyan"    },
  89:  { name: "OSPF",   color: "gold"    },
  132: { name: "SCTP",   color: "lime"    },
};

function protoTag(pr: number) {
  const p = PROTO[pr] ?? { name: `${pr}`, color: "default" };
  return <Tag color={p.color} style={{ fontFamily: "monospace", fontSize: 11 }}>{p.name}</Tag>;
}

function fmtBytes(b: number): string {
  if (b >= 1e9) return (b / 1e9).toFixed(2) + " GB";
  if (b >= 1e6) return (b / 1e6).toFixed(2) + " MB";
  if (b >= 1e3) return (b / 1e3).toFixed(1) + " KB";
  return b + " B";
}

/** Convert ISO-3166-1 alpha-2 code to a flag emoji (e.g. "VN" → "🇻🇳"). */
function ccToFlag(cc: string | undefined): string {
  if (!cc || cc.length !== 2) return "";
  // Regional indicator letters start at U+1F1E6 (= 'A')
  return String.fromCodePoint(
    0x1F1E6 - 65 + cc.toUpperCase().charCodeAt(0),
    0x1F1E6 - 65 + cc.toUpperCase().charCodeAt(1),
  );
}

function CountryTag({ cc }: { cc?: string }) {
  if (!cc) return null;
  return (
    <span
      title={cc}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: 12,
        color: "#475569",
        marginRight: 4,
        flexShrink: 0,
      }}
    >
      <span style={{ fontSize: 15, lineHeight: 1 }}>{ccToFlag(cc)}</span>
      <span style={{ fontFamily: "monospace", fontSize: 11 }}>{cc}</span>
    </span>
  );
}

function fmtTime(unix: number): string {
  const d = new Date(unix * 1000);
  return d.toTimeString().slice(0, 8) + "." + String(d.getMilliseconds()).padStart(3, "0");
}

const TCP_FLAGS: [number, string][] = [
  [0x02, "S"], [0x10, "A"], [0x01, "F"],
  [0x04, "R"], [0x08, "P"], [0x20, "U"],
];
function fmtFlags(fl: number | undefined): string {
  if (!fl) return "";
  return TCP_FLAGS.filter(([bit]) => fl & bit).map(([, c]) => c).join("");
}

const MAX_ROWS = 500; // max flows held in UI ring

// ── main component ────────────────────────────────────────────────────────────

export default function Flows() {
  const [rows, setRows]         = useState<TapFlow[]>([]);
  const [paused, setPaused]     = useState(false);
  const [connected, setConnected] = useState(false);
  const [filterSrc, setFilterSrc] = useState("");
  const [filterDst, setFilterDst] = useState("");
  const [filterProto, setFilterProto] = useState<number | null>(null);
  const [filterCC, setFilterCC] = useState("");

  const sinceRef   = useRef<number>(0);
  const esRef      = useRef<EventSource | null>(null);
  const pausedRef  = useRef(false); // ref-copy so SSE callback can read latest value
  const pendingRef = useRef<TapFlow[]>([]); // buffer flows while paused

  pausedRef.current = paused;

  const connect = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    const url = `/api/v1/live/flows?since=${sinceRef.current}&limit=200`;
    const es = new EventSource(url, { withCredentials: true });
    esRef.current = es;

    es.onopen = () => setConnected(true);
    es.onerror = () => {
      setConnected(false);
      // Auto-reconnect after 3 s
      es.close();
      esRef.current = null;
      setTimeout(connect, 3000);
    };

    es.onmessage = (ev) => {
      try {
        const batch: TapFlow[] = JSON.parse(ev.data);
        if (!batch.length) return; // heartbeat

        // Advance since to max seq in this batch
        for (const f of batch) {
          if (f.q > sinceRef.current) sinceRef.current = f.q;
        }

        if (pausedRef.current) {
          // Buffer while paused (cap at 2× MAX_ROWS to avoid OOM)
          pendingRef.current = [...pendingRef.current, ...batch].slice(-MAX_ROWS * 2);
        } else {
          setRows((prev) => {
            const next = [...prev, ...batch];
            return next.slice(-MAX_ROWS);
          });
        }
      } catch { /* ignore parse errors */ }
    };
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();
    return () => {
      esRef.current?.close();
      esRef.current = null;
    };
  }, [connect]);

  // Resume: flush pending buffer into rows
  const resume = () => {
    setPaused(false);
    const pending = pendingRef.current;
    pendingRef.current = [];
    if (pending.length) {
      setRows((prev) => [...prev, ...pending].slice(-MAX_ROWS));
    }
  };

  const clear = () => {
    setRows([]);
    pendingRef.current = [];
  };

  const reconnect = () => {
    sinceRef.current = 0;
    clear();
    connect();
  };

  // ── filter ──────────────────────────────────────────────────────────────────
  const srcLo  = filterSrc.trim().toLowerCase();
  const dstLo  = filterDst.trim().toLowerCase();
  const ccUp   = filterCC.trim().toUpperCase();
  const visible = rows.filter((r) => {
    if (srcLo && !r.si.includes(srcLo)) return false;
    if (dstLo && !r.di.includes(dstLo)) return false;
    if (filterProto !== null && r.pr !== filterProto) return false;
    if (ccUp && r.sc !== ccUp && r.dc !== ccUp) return false;
    return true;
  });

  // ── columns ─────────────────────────────────────────────────────────────────
  const columns: ColumnsType<TapFlow> = [
    {
      title: "Time",
      dataIndex: "t",
      width: 96,
      render: (t: number) => (
        <span style={{ fontFamily: "monospace", fontSize: 11, color: "#64748b" }}>
          {fmtTime(t)}
        </span>
      ),
    },
    {
      title: "Proto",
      dataIndex: "pr",
      width: 70,
      render: protoTag,
    },
    {
      title: "Source",
      key: "src",
      render: (_: unknown, r: TapFlow) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
          <CountryTag cc={r.sc} />
          <span style={{ fontFamily: "monospace", fontSize: 12 }}>
            {r.si}
            {r.sp ? <span style={{ color: "#94a3b8" }}>:{r.sp}</span> : null}
          </span>
        </span>
      ),
    },
    {
      title: "Destination",
      key: "dst",
      render: (_: unknown, r: TapFlow) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
          <CountryTag cc={r.dc} />
          <span style={{ fontFamily: "monospace", fontSize: 12 }}>
            {r.di}
            {r.dp ? <span style={{ color: "#94a3b8" }}>:{r.dp}</span> : null}
          </span>
        </span>
      ),
    },
    {
      title: "Bytes",
      dataIndex: "b",
      width: 88,
      align: "right",
      render: (b: number) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{fmtBytes(b)}</span>
      ),
    },
    {
      title: "Pkts",
      dataIndex: "p",
      width: 72,
      align: "right",
      render: (p: number) => (
        <span style={{ fontFamily: "monospace", fontSize: 12 }}>{p.toLocaleString()}</span>
      ),
    },
    {
      title: "Flags",
      dataIndex: "fl",
      width: 56,
      align: "center",
      render: (fl: number | undefined, r: TapFlow) =>
        r.pr === 6 && fl ? (
          <span style={{ fontFamily: "monospace", fontSize: 11, color: "#475569" }}>
            {fmtFlags(fl)}
          </span>
        ) : null,
    },
    {
      title: "Sample",
      dataIndex: "sr",
      width: 72,
      align: "right",
      render: (sr: number | undefined) =>
        sr ? (
          <span style={{ fontSize: 11, color: "#94a3b8" }}>1:{sr}</span>
        ) : null,
    },
  ];

  const pendingCount = pendingRef.current.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <WifiOutlined style={{ fontSize: 18, color: connected ? "#22c55e" : "#ef4444" }} />
          <Text strong style={{ fontSize: 16 }}>Live Flows</Text>
          <Badge
            count={visible.length}
            overflowCount={999}
            style={{ backgroundColor: "#3b82f6" }}
          />
          {connected ? (
            <Tag color="success" style={{ fontSize: 11 }}>connected</Tag>
          ) : (
            <Tag color="error" style={{ fontSize: 11 }}>reconnecting…</Tag>
          )}
        </div>

        <div style={{ flex: 1 }} />

        {/* Filter controls */}
        <Space wrap>
          <Input
            size="small"
            placeholder="Filter src IP"
            value={filterSrc}
            onChange={(e) => setFilterSrc(e.target.value)}
            allowClear
            style={{ width: 140 }}
          />
          <Input
            size="small"
            placeholder="Filter dst IP"
            value={filterDst}
            onChange={(e) => setFilterDst(e.target.value)}
            allowClear
            style={{ width: 140 }}
          />
          <Select
            size="small"
            placeholder="Protocol"
            allowClear
            value={filterProto ?? undefined}
            onChange={(v) => setFilterProto(v ?? null)}
            style={{ width: 100 }}
            options={Object.entries(PROTO).map(([k, v]) => ({
              value: Number(k),
              label: v.name,
            }))}
          />
          <Input
            size="small"
            placeholder="Country (e.g. US)"
            value={filterCC}
            onChange={(e) => setFilterCC(e.target.value)}
            allowClear
            maxLength={2}
            style={{ width: 130 }}
            prefix={filterCC.trim().length === 2 ? (
              <span style={{ fontSize: 16 }}>{ccToFlag(filterCC.trim())}</span>
            ) : undefined}
          />
        </Space>

        {/* Action buttons */}
        <Space>
          {paused ? (
            <Tooltip title={pendingCount ? `Resume (${pendingCount} buffered)` : "Resume"}>
              <Button
                size="small"
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={resume}
              >
                Resume{pendingCount ? ` (${pendingCount})` : ""}
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title="Pause — keep scrolling without losing new flows">
              <Button
                size="small"
                icon={<PauseCircleOutlined />}
                onClick={() => setPaused(true)}
              >
                Pause
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Clear table">
            <Button size="small" icon={<ClearOutlined />} onClick={clear} />
          </Tooltip>
          <Tooltip title="Reconnect and reset">
            <Button size="small" icon={<ReloadOutlined />} onClick={reconnect} />
          </Tooltip>
        </Space>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <Table<TapFlow>
        rowKey="q"
        size="small"
        dataSource={visible}
        columns={columns}
        pagination={false}
        scroll={{ y: "calc(100vh - 200px)", x: "max-content" }}
        locale={{ emptyText: connected ? "Waiting for flows…" : "Connecting…" }}
        style={{ fontFamily: "monospace" }}
        rowClassName={() => "flow-row"}
      />
    </div>
  );
}
